import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, formatCurrencyEUR, formatDate } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";
import PortalLayout from "../../components/PortalLayout";

const FILTERS = [
  { key: "pending", label: "In attesa" },
  { key: "awaiting_kyc", label: "In attesa KYC" },
  { key: "approved", label: "Approvate" },
  { key: "rejected", label: "Non accolte" },
  { key: "all", label: "Tutte" },
];

const STATUS = {
  pending: { label: "In attesa", color: "var(--br-bronze)" },
  awaiting_kyc: { label: "In attesa di KYC", color: "#8a97b8" },
  approved: { label: "Approvata", color: "#5f8a6b" },
  rejected: { label: "Non accolta", color: "#b36b6b" },
};

export default function AdminInvestmentRequestsPage() {
  const { locale } = useI18n();
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const res = await api.get(`/admin/investment-requests${q}`);
      setItems(res.data || []);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Errore di caricamento.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => items.length, [items]);

  return (
    <PortalLayout>
      <div className="max-w-[1100px] mx-auto px-6 py-14" data-testid="admin-investment-requests-page">
        <div className="br-overline">Finance & Compliance</div>
        <h1 className="font-serif-display text-4xl mt-3 text-[color:var(--br-parchment)]">
          Richieste di sottoscrizione
        </h1>
        <p className="text-[0.9rem] text-[color:var(--br-text-dim)] mt-3 max-w-[640px] leading-relaxed">
          Verifica le richieste inviate dai clienti. All'approvazione, il cliente riceve via email le
          coordinate del bonifico (dai suoi dati bancari) e una conferma; al rifiuto riceve la nota inserita.
        </p>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="requests-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={filter === f.key ? "br-btn-primary" : "br-btn-outline"}
              style={{ padding: "8px 16px", fontSize: "0.68rem" }}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {error ? (
          <div
            className="mt-6 br-card px-6 py-4 text-[0.9rem]"
            style={{ borderLeft: "2px solid #b36b6b", color: "var(--br-parchment)" }}
            data-testid="requests-error"
          >
            {error}
          </div>
        ) : null}

        <div className="mt-8 space-y-4">
          {loading ? (
            <div className="text-[0.9rem] text-[color:var(--br-text-dim)]" data-testid="requests-loading">
              Caricamento…
            </div>
          ) : counts === 0 ? (
            <div
              className="br-card px-8 py-12 text-center text-[0.9rem] text-[color:var(--br-text-dim)]"
              data-testid="requests-empty"
            >
              Nessuna richiesta in questa vista.
            </div>
          ) : (
            items.map((r) => (
              <RequestCard key={r.id} req={r} locale={locale} onDone={load} />
            ))
          )}
        </div>
      </div>
    </PortalLayout>
  );
}

function RequestCard({ req, locale, onDone }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const s = STATUS[req.status] || STATUS.pending;
  const isPending = req.status === "pending";
  const isAwaitingKyc = req.status === "awaiting_kyc";

  const decide = async (decision) => {
    if (decision === "rejected" && !comment.trim()) {
      setErr("Inserisci una nota per il rifiuto.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/admin/investment-requests/${req.id}/decision`, {
        decision,
        comment: comment.trim() || null,
      });
      await onDone();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Errore.");
      setBusy(false);
    }
  };

  return (
    <div className="br-card px-7 py-6" data-testid={`admin-request-${req.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[1.05rem] text-[color:var(--br-parchment)]">{req.plan?.name || "—"}</div>
          <div className="text-[0.82rem] text-[color:var(--br-text-dim)] mt-1">
            {req.user_name} · {req.user_email}
          </div>
          <div className="text-[0.8rem] text-[color:var(--br-text-dim)] mt-2 flex flex-wrap gap-x-5 gap-y-1">
            <span>
              Rendimento lordo atteso:{" "}
              <span className="text-[color:var(--br-parchment)]">
                {(req.plan?.gross_rate ?? 0).toFixed?.(2) ?? req.plan?.gross_rate} %
              </span>
            </span>
            <span>
              Importo:{" "}
              <span className="text-[color:var(--br-parchment)]">
                {req.amount ? formatCurrencyEUR(req.amount, locale) : "—"}
              </span>
            </span>
            <span>Inviata: {formatDate(req.created_at, locale)}</span>
            <span data-testid={`admin-request-kid-${req.id}`}>
              KID:{" "}
              {req.kid_ack?.accepted ? (
                <span style={{ color: "#5f8a6b" }}>
                  presa visione · {formatDate(req.kid_ack.at, locale)}
                </span>
              ) : (
                <span style={{ color: "var(--br-text-dim)" }}>—</span>
              )}
            </span>
          </div>
          {req.note ? (
            <div className="text-[0.82rem] text-[color:var(--br-text-dim)] mt-2 italic max-w-[620px]">
              Nota cliente: “{req.note}”
            </div>
          ) : null}
          {req.decision_comment && !isPending && !isAwaitingKyc ? (
            <div className="text-[0.82rem] text-[color:var(--br-text-dim)] mt-2 italic max-w-[620px]">
              Nota team: “{req.decision_comment}”
            </div>
          ) : null}
        </div>
        <span
          className="font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full flex-shrink-0"
          style={{ color: s.color, border: `1px solid ${s.color}` }}
          data-testid={`admin-request-status-${req.id}`}
        >
          {s.label}
        </span>
      </div>

      {isPending || isAwaitingKyc ? (
        <div className="mt-5 pt-5" style={{ borderTop: "1px solid var(--br-line)" }}>
          {isAwaitingKyc ? (
            <div
              className="text-[0.82rem] mb-3 px-4 py-2.5 border-l-2"
              style={{ borderColor: "#8a97b8", color: "var(--br-text-dim)" }}
              data-testid={`admin-request-kyc-note-${req.id}`}
            >
              Il cliente non ha ancora completato la verifica KYC: la richiesta diventerà
              lavorabile automaticamente dopo l&apos;approvazione dei 6 documenti.
            </div>
          ) : null}
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            maxLength={1200}
            placeholder="Nota per il cliente (obbligatoria in caso di rifiuto)"
            className="w-full bg-transparent border rounded px-4 py-2.5 text-[color:var(--br-parchment)] outline-none resize-none text-[0.88rem]"
            style={{ borderColor: "var(--br-line)" }}
            data-testid={`admin-request-comment-${req.id}`}
          />
          {err ? (
            <div className="text-[0.82rem] mt-2" style={{ color: "#d98a8a" }}>
              {err}
            </div>
          ) : null}
          <div className="mt-3 flex items-center gap-3 justify-end">
            <button
              type="button"
              onClick={() => decide("rejected")}
              disabled={busy}
              className="br-btn-outline"
              data-testid={`admin-request-reject-${req.id}`}
            >
              Rifiuta
            </button>
            {isPending ? (
              <button
                type="button"
                onClick={() => decide("approved")}
                disabled={busy}
                className="br-btn-primary"
                data-testid={`admin-request-approve-${req.id}`}
              >
                {busy ? "…" : "Approva e invia coordinate"}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
